import React, { useState } from 'react';
import { NextUI } from '@/components/NextUI';
import { useAppDispatch } from '@/hooks/redux';
import { login } from '@/store/slices/authSlice';
import { showModal } from '@/store/slices/modalSlice';
import { emit } from '@/utils/emitter';

const DownloadLoginModal = ({
  data,
  hideModal,
}: {
  data?: any;
  hideModal: () => void;
}) => {
  const [email, setEmail] = useState(data?.initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const handleClose = React.useCallback(() => {
    data?.onCancel?.();
    hideModal();
  }, [data, hideModal]);

  const handleLogin = async () => {
    if (!email || !password) return;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        login({
          email,
          password,
        })
      ).unwrap();
      hideModal();

      if (data?.onSuccess) {
        await data.onSuccess();
      } else {
        emit('download:step-completed', {
          extension: data?.extension,
          toolType: data?.toolType,
          originEntryPoint: data?.originEntryPoint,
        });
      }
    } catch (err: any) {
      setError(err || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const openSignup = () => {
    dispatch(showModal({ modalKey: 'DownloadSignup', modalData: data }));
  };

  return (
    <NextUI.Modal
      className="max-w-[500px] max-h-[90vh] rounded-none"
      classNames={{ content: 'py-8 px-8 !overflow-y-auto' }}
      defaultOpen={true}
      name={'DownloadLogin'}
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={false}
      onClose={handleClose}
    >
      <div className="flex flex-col items-start">
        <h2 className="text-[28px] font-bold text-dark text-start leading-tight mb-[10px]">
          Login to download your file
        </h2>
        <p className="text-gray-medium text-start mb-4">
          Welcome back! Please enter your details.
        </p>

        <div className="w-full space-y-5">
          <NextUI.Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outline"
            radius="sm"
            className="w-full"
            classNames={{
              input: 'text-base md:text-sm placeholder:text-gray-medium',
              inputWrapper:
                'bg-white border border-gray-200 h-12 shadow-none hover:border-blue-500 focus-within:border-blue-500 transition-colors',
            }}
          />

          <NextUI.Input
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outline"
            radius="sm"
            className="w-full"
            classNames={{
              input: 'text-base md:text-sm placeholder:text-gray-400',
              inputWrapper:
                'bg-white border border-gray-200 h-12 shadow-none hover:border-blue-500 focus-within:border-blue-500 transition-colors',
            }}
          />

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <NextUI.Button
            className="w-full h-12 text-white font-semibold text-lg transition-colors"
            onClick={handleLogin}
            isLoading={isLoading}
          >
            Log In
          </NextUI.Button>
        </div>

        <div className="mt-3 border-t border-gray-100 w-full pt-3 text-start">
          <p className="text-gray-medium text-sm">
            Don't have an account?{' '}
            <button
              onClick={openSignup}
              className="text-primary-blue font-semibold hover:underline"
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default DownloadLoginModal;
